/**
 * B2BSalesRegisterGST.tsx  —  B2B Sales Register (GST) Report
 * ────────────────────────────────────────────────────────────────────────────
 * - Lists only B2B invoices (buyer has a valid GSTIN)
 * - Columns: Sl.No, Invoice No, Invoice Date, Customer Name, GSTIN, State,
 *   Place of Supply, Taxable Value, GST Rate, CGST, SGST, IGST, Invoice Value
 * - Report Summary: Total Taxable Value, Total CGST, Total SGST, Total IGST,
 *   Total Invoice Value
 * - Filters: Month, Financial Year, custom From/To date, Customer/GSTIN
 *   search, State
 * - Defaults to CURRENT MONTH data on first load; any filter change re-queries
 *   the server accordingly (month-wise, FY-wise, custom range, etc.)
 * - Pagination (server-driven)
 * - PDF / Excel / CSV export — always exports the FULL filtered dataset
 *   (not just the current page) with the organisation name printed on the
 *   report, for accurate CA / GSTR-1 filing purposes
 * - Bold report heading & bold table headers throughout
 *
 * Dependencies (install if not already present):
 *   npm i jspdf jspdf-autotable exceljs file-saver date-fns
 *   npm i @mui/x-data-grid @mui/material @mui/icons-material
 * ────────────────────────────────────────────────────────────────────────────
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import AssessmentIcon      from "@mui/icons-material/Assessment";
import ClearIcon           from "@mui/icons-material/Clear";
import DownloadIcon        from "@mui/icons-material/Download";
import FilterListIcon      from "@mui/icons-material/FilterList";
import GridOnIcon          from "@mui/icons-material/GridOn";
import PictureAsPdfIcon    from "@mui/icons-material/PictureAsPdf";
import ReceiptLongIcon     from "@mui/icons-material/ReceiptLong";
import RefreshIcon         from "@mui/icons-material/Refresh";
import SearchIcon          from "@mui/icons-material/Search";

import Footer from "../Footer/Footer";
import { getOrganizationDetails } from "../Services/orgService";
import {
  getB2BSalesRegister,
  getB2BSalesRegisterExport,
  IB2BSalesRegisterFilters,
} from "../Services/B2BSalesRegisterService";
import { IOrgDetails } from "../Interfaces/IorgProfile";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface IB2BSalesRegisterRow {
  slNo?: number;
  invoiceId: string;
  invoiceDate: string;
  financialYear?: string;
  customerId?: string;
  customerName: string;
  GSTIN: string;
  stateCode: string;
  stateName: string;
  placeOfSupply: string;
  taxableValue: number;
  gstRate: number;
  cgst: number;
  sgst: number;
  igst: number;
  invoiceValue: number;
  invoiceType?: string;
}

export interface IB2BSalesRegisterSummary {
  totalTaxableValue: number;
  totalCGST: number;
  totalSGST: number;
  totalIGST: number;
  totalInvoiceValue: number;
  totalInvoices: number;
}

const EMPTY_SUMMARY: IB2BSalesRegisterSummary = {
  totalTaxableValue: 0,
  totalCGST: 0,
  totalSGST: 0,
  totalIGST: 0,
  totalInvoiceValue: 0,
  totalInvoices: 0,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtINR = (n: any) =>
  `Rs. ${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const fmtNum = (n: any) => Number(n ?? 0).toFixed(2);

const fmtDate = (s?: string): string => {
  if (!s) return "—";
  try {
    return format(parseISO(s), "dd/MM/yyyy");
  } catch {
    return s;
  }
};

/** Current month as "YYYY-MM" (used as the default filter) */
const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** Builds "YYYY-YY" financial-year options for the last ~6 years */
const buildFYOptions = () => {
  const now = new Date();
  const currentFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options: { value: string; label: string }[] = [];
  for (let i = 0; i < 6; i++) {
    const startYear = currentFYStart - i;
    const value = `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
    options.push({ value, label: `FY ${value}` });
  }
  return options;
};

const GST_STATE_OPTIONS: { code: string; name: string }[] = [
  { code: "01", name: "Jammu & Kashmir" }, { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" }, { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" }, { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" }, { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" }, { code: "10", name: "Bihar" },
  { code: "11", name: "Sikkim" }, { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" }, { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" }, { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" }, { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" }, { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" }, { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" }, { code: "24", name: "Gujarat" },
  { code: "25", name: "Daman & Diu" }, { code: "26", name: "Dadra & Nagar Haveli" },
  { code: "27", name: "Maharashtra" }, { code: "28", name: "Andhra Pradesh (Old)" },
  { code: "29", name: "Karnataka" }, { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" }, { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" }, { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman & Nicobar Islands" }, { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh (New)" }, { code: "38", name: "Ladakh" },
];

const FILTER_MODE_OPTIONS = [
  { value: "month", label: "Month-wise" },
  { value: "fy",    label: "Financial Year" },
  { value: "range", label: "Custom Date Range" },
];

// ─── Summary Card ─────────────────────────────────────────────────────────────

interface SummaryCardProps {
  label: string; value: string; color: string; border: string; loading: boolean;
}
const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, color, border, loading }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: border, height: "100%" }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      {loading ? (
        <>
          <Skeleton width="60%" height={16} />
          <Skeleton width="80%" height={28} sx={{ mt: 0.5 }} />
        </>
      ) : (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
          <Typography variant="h6" fontWeight={700} color={color}>{value}</Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// ─── Mobile Row Card ──────────────────────────────────────────────────────────

const MobileCard: React.FC<{ row: IB2BSalesRegisterRow; sno: number }> = ({ row, sno }) => (
  <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2, "&:hover": { boxShadow: 2 } }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ bgcolor: "grey.100", px: 0.8, py: 0.2, borderRadius: 1, fontWeight: 700, color: "text.secondary" }}>
            #{sno}
          </Typography>
          <Typography variant="body2" fontWeight={700} color="primary.main">{row.invoiceId}</Typography>
        </Stack>
        <Chip label={fmtDate(row.invoiceDate)} size="small" variant="outlined" sx={{ fontSize: 10 }} />
      </Stack>
      <Typography variant="body2" fontWeight={600} noWrap mb={0.25}>{row.customerName}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        GSTIN: {row.GSTIN} · {row.placeOfSupply}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Grid container spacing={1}>
        {[
          { l: "Taxable",  v: fmtINR(row.taxableValue) },
          { l: "GST Rate", v: `${row.gstRate}%` },
          { l: "CGST",     v: fmtINR(row.cgst) },
          { l: "SGST",     v: fmtINR(row.sgst) },
          { l: "IGST",     v: fmtINR(row.igst) },
          { l: "Invoice Value", v: fmtINR(row.invoiceValue), bold: true },
        ].map(({ l, v, bold }) => (
          <Grid item xs={4} key={l}>
            <Typography variant="caption" color="text.secondary" display="block">{l}</Typography>
            <Typography variant="caption" fontWeight={bold ? 700 : 500}>{v}</Typography>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </Card>
);

// ─── PDF Export (full filtered dataset) ──────────────────────────────────────

function exportPDF(
  rows: IB2BSalesRegisterRow[],
  summary: IB2BSalesRegisterSummary,
  orgDetails: IOrgDetails | null,
  periodLabel: string
) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.width;
  const H = doc.internal.pageSize.height;

  // Header banner
  doc.setFillColor(67, 56, 202); // indigo
  doc.rect(0, 0, W, 26, "F");

  doc.setFontSize(15); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
  doc.text((orgDetails as any)?.OrgName || "Organisation", 14, 10);

  doc.setFontSize(8.5); doc.setFont("helvetica", "normal");
  const addr = [
    (orgDetails as any)?.address?.[0]?.addressLine1,
    (orgDetails as any)?.address?.[0]?.city,
    (orgDetails as any)?.address?.[0]?.state,
    (orgDetails as any)?.address?.[0]?.zipCode,
  ].filter(Boolean).join(", ");
  doc.text(addr || "", 14, 15.5);
  doc.text(`GSTIN: ${(orgDetails as any)?.GST || "N/A"}`, 14, 20.5);

  doc.setFontSize(13); doc.setFont("helvetica", "bold");
  doc.text("B2B SALES REGISTER (GST)", W - 14, 10, { align: "right" });
  doc.setFontSize(8.5); doc.setFont("helvetica", "normal");
  doc.text(`Period: ${periodLabel}`, W - 14, 15.5, { align: "right" });
  doc.text(`Generated: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, W - 14, 20.5, { align: "right" });

  doc.setTextColor(0);

  // Summary strip
  let y = 32;
  const sumCards = [
    { l: "Total Invoices",     v: summary.totalInvoices.toString() },
    { l: "Total Taxable Value",v: fmtINR(summary.totalTaxableValue) },
    { l: "Total CGST",         v: fmtINR(summary.totalCGST) },
    { l: "Total SGST",         v: fmtINR(summary.totalSGST) },
    { l: "Total IGST",         v: fmtINR(summary.totalIGST) },
    { l: "Total Invoice Value",v: fmtINR(summary.totalInvoiceValue) },
  ];
  const cardW = (W - 28) / sumCards.length;
  sumCards.forEach((c, i) => {
    const x = 14 + i * cardW;
    doc.setDrawColor(200, 200, 200);
    doc.rect(x, y, cardW - 2, 13);
    doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(100);
    doc.text(c.l, x + 2, y + 4.5);
    doc.setFontSize(9.5); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
    doc.text(c.v, x + 2, y + 10.5);
  });

  y = 50;

  const head = [[
    "Sl.No", "Invoice No", "Invoice Date", "Customer Name", "GSTIN",
    "State", "POS", "Taxable Amt", "GST(%)",
    "CGST", "SGST", "IGST", "Total Amt",  
  ]];

  const body = rows.map((r, i) => [
    r.slNo ?? i + 1,
    r.invoiceId,
    fmtDate(r.invoiceDate),
    r.customerName,
    r.GSTIN,
    r.stateName,
    r.placeOfSupply,
    fmtNum(r.taxableValue),
    fmtNum(r.gstRate),
    fmtNum(r.cgst),
    fmtNum(r.sgst),
    fmtNum(r.igst),
    fmtNum(r.invoiceValue),
  ]);

  // Totals row
  body.push([
    "", "", "", "", "", "", "TOTAL",
    fmtNum(summary.totalTaxableValue), "",
    fmtNum(summary.totalCGST), fmtNum(summary.totalSGST), fmtNum(summary.totalIGST),
    fmtNum(summary.totalInvoiceValue),
  ]);

  autoTable(doc, {
    head,
    body,
    startY: y,
    theme: "grid",
    styles: { fontSize: 7.5, cellPadding: 1.5, textColor: [30, 30, 30] },
    headStyles: { fillColor: [67, 56, 202], textColor: 255, fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 245, 255] },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 28 },
      2: { cellWidth: 22 },
      3: { cellWidth: 38 },
      4: { cellWidth: 28 },
      5: { cellWidth: 18 },
      6: { cellWidth: 20 },
      7: { cellWidth: 20, halign: "right" },
      8: { cellWidth: 14, halign: "right" },
      9: { cellWidth: 16, halign: "right" },
      10: { cellWidth: 16, halign: "right" },
      11: { cellWidth: 16, halign: "right" },
      12: { cellWidth: 22, halign: "right", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      // Bold the totals row (last row)
      if (data.row.index === body.length - 1 && data.section === "body") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [230, 230, 250];
      }
    },
    didDrawPage: (data) => {
      doc.setFontSize(7); doc.setTextColor(150);
      doc.text(
        `Page ${data.pageNumber} of ${doc.getNumberOfPages()}  |  ${(orgDetails as any)?.OrgName || ""}  |  B2B SALES REGISTER (GST)`,
        W / 2, H - 4, { align: "center" }
      );
      doc.setTextColor(0);
    },
  });

  doc.save(`B2B_SalesRegister_GST_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
}

// ─── Excel Export (bold headings, org name, full filtered dataset) ─────────

async function exportExcel(
  rows: IB2BSalesRegisterRow[],
  summary: IB2BSalesRegisterSummary,
  orgDetails: IOrgDetails | null,
  periodLabel: string
) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("B2B Sales Register");

  const columns = [
    "Sl.No", "Invoice No", "Invoice Date", "Customer Name", "GSTIN",
    "State", "Place of Supply", "Taxable Amt", "GST(%)",
    "CGST", "SGST", "IGST", "Invoice Value",
  ];

  // Org name (bold, large)
  sheet.mergeCells(1, 1, 1, columns.length);
  const orgCell = sheet.getCell(1, 1);
  orgCell.value = (orgDetails as any)?.OrgName || "Organisation";
  orgCell.font = { bold: true, size: 14 };

  // GSTIN of org
  sheet.mergeCells(2, 1, 2, columns.length);
  const gstCell = sheet.getCell(2, 1);
  gstCell.value = `GSTIN: ${(orgDetails as any)?.GST || "N/A"}`;
  gstCell.font = { bold: false, size: 10 };

  // Report title (bold)
  sheet.mergeCells(3, 1, 3, columns.length);
  const titleCell = sheet.getCell(3, 1);
  titleCell.value = "B2B SALES REGISTER (GST)";
  titleCell.font = { bold: true, size: 13 };

  // Period
  sheet.mergeCells(4, 1, 4, columns.length);
  const periodCell = sheet.getCell(4, 1);
  periodCell.value = `Period: ${periodLabel}   |   Generated: ${format(new Date(), "dd/MM/yyyy HH:mm")}`;
  periodCell.font = { italic: true, size: 9, color: { argb: "FF666666" } };

  sheet.addRow([]); // blank spacer row (row 5)

  // Header row (bold)
  const headerRow = sheet.addRow(columns);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4338CA" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" }, left: { style: "thin" },
      bottom: { style: "thin" }, right: { style: "thin" },
    };
  });

  // Data rows
  rows.forEach((r, i) => {
    sheet.addRow([
      r.slNo ?? i + 1,
      r.invoiceId,
      fmtDate(r.invoiceDate),
      r.customerName,
      r.GSTIN,
      r.stateName,
      r.placeOfSupply,
      Number(r.taxableValue.toFixed(2)),
      Number(r.gstRate.toFixed(2)),
      Number(r.cgst.toFixed(2)),
      Number(r.sgst.toFixed(2)),
      Number(r.igst.toFixed(2)),
      Number(r.invoiceValue.toFixed(2)),
    ]);
  });

  // Totals row (bold)
  const totalsRow = sheet.addRow([
    "", "", "", "", "", "", "TOTAL",
    Number(summary.totalTaxableValue.toFixed(2)), "",
    Number(summary.totalCGST.toFixed(2)),
    Number(summary.totalSGST.toFixed(2)),
    Number(summary.totalIGST.toFixed(2)),
    Number(summary.totalInvoiceValue.toFixed(2)),
  ]);
  totalsRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6FA" } };
  });

  // Column widths
  sheet.columns = columns.map((c) => ({
    width: c === "Customer Name" || c === "Place of Supply" ? 26 : c === "GSTIN" ? 20 : 16,
  }));

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    `B2B_SalesRegister_GST_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`
  );
}

// ─── CSV Export (full filtered dataset) ──────────────────────────────────────

function exportCSV(rows: IB2BSalesRegisterRow[], orgDetails: IOrgDetails | null, periodLabel: string) {
  const headers = [
    "Sl.No", "Invoice No", "Invoice Date", "Customer Name", "GSTIN",
    "State", "Place of Supply", "Taxable Value", "GST (%)",
    "CGST", "SGST", "IGST", "Invoice Value",
  ];
  const data = rows.map((r, i) => [
    r.slNo ?? i + 1, r.invoiceId, fmtDate(r.invoiceDate), r.customerName, r.GSTIN,
    r.stateName, r.placeOfSupply, r.taxableValue, r.gstRate, r.cgst, r.sgst, r.igst, r.invoiceValue,
  ]);
  const metaLines = [
    [(orgDetails as any)?.OrgName || "Organisation"],
    [`GSTIN: ${(orgDetails as any)?.GST || "N/A"}`],
    ["B2B SALES REGISTER (GST)"],
    [`Period: ${periodLabel}`],
    [],
  ];
  const csv = [...metaLines, headers, ...data]
    .map((row) => row.map((c) => `"${c ?? ""}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `B2B_SalesRegister_GST_${format(new Date(), "yyyyMMdd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main Component ───────────────────────────────────────────────────────────

const B2BSalesRegisterGST: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const orgId = localStorage.getItem("orgId") ?? "";
  const [orgDetails, setOrgDetails] = useState<IOrgDetails | null>(null);

  // Data
  const [rows, setRows] = useState<IB2BSalesRegisterRow[]>([]);
  const [summary, setSummary] = useState<IB2BSalesRegisterSummary>(EMPTY_SUMMARY);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [filterMode, setFilterMode] = useState<"month" | "fy" | "range">("month");
  const [month, setMonth] = useState(currentMonthStr()); // default: current month
  const [financialYear, setFinancialYear] = useState(buildFYOptions()[0].value);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [gstin, setGstin] = useState("");
  const [stateCode, setStateCode] = useState("all");
  const [showFilters, setShowFilters] = useState(!isMobile);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState("invoiceDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const fyOptions = useMemo(buildFYOptions, []);

  const hasFilters = !!(search || gstin || (stateCode !== "all") || (filterMode === "range" && (fromDate || toDate)));
  const serialOffset = (page - 1) * pageSize;

  const periodLabel = useMemo(() => {
    if (filterMode === "month") return format(parseISO(`${month}-01`), "MMMM yyyy");
    if (filterMode === "fy") return `FY ${financialYear}`;
    if (fromDate || toDate) return `${fromDate || "…"} to ${toDate || "…"}`;
    return "All dates";
  }, [filterMode, month, financialYear, fromDate, toDate]);

  // Build the filter payload sent to the backend based on current filter mode
  const buildFilterPayload = useCallback((): IB2BSalesRegisterFilters => {
    const base: IB2BSalesRegisterFilters = {
      search: search.trim() || undefined,
      gstin: gstin.trim() || undefined,
      state: stateCode !== "all" ? stateCode : undefined,
      sortField,
      sortOrder,
    };
    if (filterMode === "month") return { ...base, month };
    if (filterMode === "fy") return { ...base, financialYear };
    return { ...base, fromDate: fromDate || undefined, toDate: toDate || undefined };
  }, [filterMode, month, financialYear, fromDate, toDate, search, gstin, stateCode, sortField, sortOrder]);

  // Org details
  useEffect(() => {
    const run = async () => {
      if (!orgId) return;
      try {
        const data = await getOrganizationDetails(orgId);
        setOrgDetails(data);
      } catch (err) {
        console.error("Failed to load org details:", err);
        const s = localStorage.getItem("orgDetails");
        if (s) setOrgDetails(JSON.parse(s));
      }
    };
    run();
  }, [orgId]);

  // Main paginated fetch — separate function as requested, reusable for refresh
  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setError("");
    try {
      const filters = buildFilterPayload();
      const data = await getB2BSalesRegister(orgId, { ...filters, page, limit: pageSize });
      setRows(data.invoices ?? []);
      setSummary(data.summary ?? EMPTY_SUMMARY);
      setTotalCount(data.pagination?.total ?? 0);
    } catch (err: any) {
      setError(err?.message || "Failed to load B2B sales register.");
    } finally {
      setLoading(false);
    }
  }, [orgId, buildFilterPayload, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetPage = () => setPage(1);

  const clearFilters = () => {
    setFilterMode("month");
    setMonth(currentMonthStr());
    setFromDate(""); setToDate("");
    setSearch(""); setGstin(""); setStateCode("all");
    resetPage();
  };

  // Separate function: fetches ALL filtered rows (unpaginated) for exports
  const fetchAllForExport = useCallback(async (): Promise<{
    rows: IB2BSalesRegisterRow[]; summary: IB2BSalesRegisterSummary;
  }> => {
    const filters = buildFilterPayload();
    const data = await getB2BSalesRegisterExport(orgId, filters);
    return { rows: data.invoices ?? [], summary: data.summary ?? EMPTY_SUMMARY };
  }, [orgId, buildFilterPayload]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { rows: allRows, summary: allSummary } = await fetchAllForExport();
      exportPDF(allRows, allSummary, orgDetails, periodLabel);
    } catch (e: any) {
      setError(e?.message || "Failed to export PDF.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const { rows: allRows, summary: allSummary } = await fetchAllForExport();
      await exportExcel(allRows, allSummary, orgDetails, periodLabel);
    } catch (e: any) {
      setError(e?.message || "Failed to export Excel.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const { rows: allRows } = await fetchAllForExport();
      exportCSV(allRows, orgDetails, periodLabel);
    } catch (e: any) {
      setError(e?.message || "Failed to export CSV.");
    } finally {
      setExporting(false);
    }
  };

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const columns: GridColDef[] = [
    {
      field: "slNo", headerName: "Sl. No.", width: 80, sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary" fontSize={12}>{p.value}</Typography>
      ),
    },
    {
      field: "invoiceId", headerName: "Invoice No.", width: 150,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={700} color="primary.main" fontSize={12}>{p.value}</Typography>
      ),
    },
    { field: "invoiceDate", headerName: "Invoice Date", width: 115, valueFormatter: (v: any) => fmtDate(v) },
    { field: "customerName", headerName: "Customer Name", flex: 1, minWidth: 180 },
    { field: "GSTIN", headerName: "GSTIN", width: 160 },
    { field: "stateName", headerName: "State", width: 140 },
    { field: "placeOfSupply", headerName: "Place of Supply", width: 170 },
    {
      field: "taxableValue", headerName: "Taxable Value", width: 130, type: "number",
      headerAlign: "right", align: "right", valueFormatter: (v: any) => fmtNum(v),
    },
    {
      field: "gstRate", headerName: "GST Rate (%)", width: 110, type: "number",
      headerAlign: "right", align: "right", valueFormatter: (v: any) => `${fmtNum(v)}%`,
    },
    { field: "cgst", headerName: "CGST", width: 100, type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => fmtNum(v) },
    { field: "sgst", headerName: "SGST", width: 100, type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => fmtNum(v) },
    { field: "igst", headerName: "IGST", width: 100, type: "number", headerAlign: "right", align: "right", valueFormatter: (v: any) => fmtNum(v) },
    {
      field: "invoiceValue", headerName: "Invoice Value", width: 135, type: "number",
      headerAlign: "right", align: "right",
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={700} fontSize={12}>{fmtINR(p.value)}</Typography>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-location">
        <span>Reports &gt; B2B Sales Register (GST)</span>
      </div>

      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 1 }}>

        {/* ── Page Header ────────────────────────────────────────────────── */}
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReceiptLongIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={700}>
                B2B Sales Register (GST)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalCount > 0
                  ? `${totalCount} invoices · Page ${page} of ${totalPages} · ${periodLabel}`
                  : `Loading… · ${periodLabel}`}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Tooltip title="Export full filtered report to PDF">
              <Button
                size="small" variant="outlined" color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportPDF}
                disabled={loading || exporting || totalCount === 0}
              >
                {isMobile ? "PDF" : "Export PDF"}
              </Button>
            </Tooltip>
            <Tooltip title="Export full filtered report to Excel">
              <Button
                size="small" variant="outlined" color="success"
                startIcon={<GridOnIcon />}
                onClick={handleExportExcel}
                disabled={loading || exporting || totalCount === 0}
              >
                {isMobile ? "Excel" : "Export Excel"}
              </Button>
            </Tooltip>
            <Tooltip title="Export full filtered report to CSV">
              <Button
                size="small" variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={loading || exporting || totalCount === 0}
              >
                CSV
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchData}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}
            action={<Button size="small" onClick={fetchData}>Retry</Button>}>
            {error}
          </Alert>
        )}

        {/* ── Report Summary ─────────────────────────────────────────────── */}
        <Grid container spacing={1.5} mb={2}>
          {[
            { label: "Total Taxable Value", value: fmtINR(summary.totalTaxableValue), color: "primary.main",  border: "primary.light" },
            { label: "Total CGST",          value: fmtINR(summary.totalCGST),         color: "info.main",     border: "info.light" },
            { label: "Total SGST",          value: fmtINR(summary.totalSGST),         color: "info.main",     border: "info.light" },
            { label: "Total IGST",          value: fmtINR(summary.totalIGST),         color: "warning.main",  border: "warning.light" },
            { label: "Total Invoice Value", value: fmtINR(summary.totalInvoiceValue), color: "success.main",  border: "success.light" },
          ].map((c) => (
            <Grid item xs={6} sm={4} md={2.4} key={c.label}>
              <SummaryCard {...c} loading={loading} />
            </Grid>
          ))}
        </Grid>

        {/* ── Search + Filter Toggle ─────────────────────────────────────── */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} mb={1.5}>
          <TextField
            size="small"
            placeholder="Search invoice no. or customer name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(""); resetPage(); }}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flex: 1, maxWidth: { sm: 380 } }}
          />
          <Button
            size="small" startIcon={<FilterListIcon />}
            variant={hasFilters ? "contained" : "outlined"}
            color={hasFilters ? "primary" : "inherit"}
            onClick={() => setShowFilters((v) => !v)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {showFilters ? "Hide Filters" : `Filters${hasFilters ? " ●" : ""}`}
          </Button>
        </Stack>

        {/* ── Filter Panel ───────────────────────────────────────────────── */}
        <Collapse in={showFilters}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "grey.50" }}>
            <Grid container spacing={1.5} alignItems="flex-end">

              {/* Filter mode */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Filter By</InputLabel>
                  <Select
                    value={filterMode} label="Filter By"
                    onChange={(e: SelectChangeEvent) => { setFilterMode(e.target.value as any); resetPage(); }}
                  >
                    {FILTER_MODE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              {/* Month picker */}
              {filterMode === "month" && (
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Month" type="month" size="small" fullWidth
                    value={month}
                    onChange={(e) => { setMonth(e.target.value); resetPage(); }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}

              {/* FY picker */}
              {filterMode === "fy" && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Financial Year</InputLabel>
                    <Select
                      value={financialYear} label="Financial Year"
                      onChange={(e: SelectChangeEvent) => { setFinancialYear(e.target.value); resetPage(); }}
                    >
                      {fyOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Custom range */}
              {filterMode === "range" && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="From Date" type="date" size="small" fullWidth
                      value={fromDate}
                      onChange={(e) => { setFromDate(e.target.value); resetPage(); }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: toDate || undefined }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="To Date" type="date" size="small" fullWidth
                      value={toDate}
                      onChange={(e) => { setToDate(e.target.value); resetPage(); }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: fromDate || undefined }}
                    />
                  </Grid>
                </>
              )}

              {/* GSTIN search */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="GSTIN" size="small" fullWidth
                  value={gstin}
                  onChange={(e) => { setGstin(e.target.value); resetPage(); }}
                  placeholder="Search by GSTIN"
                />
              </Grid>

              {/* State filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={stateCode} label="State"
                    onChange={(e: SelectChangeEvent) => { setStateCode(e.target.value); resetPage(); }}
                  >
                    <MenuItem value="all">All States</MenuItem>
                    {GST_STATE_OPTIONS.map((s) => (
                      <MenuItem key={s.code} value={s.code}>{s.code} - {s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {hasFilters && (
                <Grid item xs={12} sm={6} md={1}>
                  <Button variant="outlined" color="error" size="small"
                    startIcon={<ClearIcon />} onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
              )}
            </Grid>

            {/* Rows per page */}
            <Stack direction="row" alignItems="center" spacing={1} mt={1.5}>
              <Typography variant="caption" color="text.secondary">Rows per page:</Typography>
              <Select
                size="small" value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); resetPage(); }}
                sx={{ fontSize: 13, minWidth: 75 }}
              >
                {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
              {totalCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Showing {serialOffset + 1}–{Math.min(serialOffset + pageSize, totalCount)} of {totalCount}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Collapse>

        {/* ── Mobile: Card list ──────────────────────────────────────────── */}
        {isMobile ? (
          <>
            {loading
              ? [1, 2, 3, 4, 5].map((k) => <Skeleton key={k} variant="rounded" height={150} sx={{ mb: 1.5, borderRadius: 2 }} />)
              : rows.length === 0
                ? (
                  <Box textAlign="center" py={6}>
                    <ReceiptLongIcon sx={{ fontSize: 48, color: "grey.300", mb: 1 }} />
                    <Typography color="text.secondary">No B2B invoices found for this period</Typography>
                    {hasFilters && <Button size="small" onClick={clearFilters} sx={{ mt: 1 }}>Clear filters</Button>}
                  </Box>
                )
                : rows.map((row, idx) => (
                    <MobileCard key={row.invoiceId} row={row} sno={row.slNo ?? serialOffset + idx + 1} />
                  ))
            }
          </>
        ) : (
          /* ── Desktop: DataGrid ─────────────────────────────────────────── */
          <DataGrid
            rows={rows.map((r) => ({ ...r, id: r.invoiceId }))}
            columns={columns}
            getRowId={(r) => `${r.invoiceId}_${r.slNo}`}
            loading={loading}
            paginationMode="client"
            pageSizeOptions={[]}
            hideFooterPagination
            hideFooter
            autoHeight
            disableRowSelectionOnClick
            density="compact"
            sortingMode="server"
            onSortModelChange={(model) => {
              if (model.length > 0) {
                setSortField(model[0].field);
                setSortOrder(model[0].sort ?? "desc");
              } else {
                setSortField("invoiceDate");
                setSortOrder("desc");
              }
              resetPage();
            }}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              "& .MuiDataGrid-columnHeaders": { backgroundColor: "#4338CA", minHeight: "44px !important", borderBottom: "none" },
              "& .MuiDataGrid-columnHeader": { backgroundColor: "#4338CA", color: "#FFFFFF" },
              "& .MuiDataGrid-columnHeaderTitle": { color: "#FFFFFF !important", fontWeight: 700, fontSize: 12 },
              "& .MuiDataGrid-sortIcon": { color: "#FFFFFF !important", opacity: 1 },
              "& .MuiDataGrid-menuIconButton": { color: "#FFFFFF !important", opacity: 1 },
              "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.2)" },
              "& .MuiDataGrid-cell": { fontSize: 12 },
            }}
          />
        )}

        {/* ── Pagination Footer ──────────────────────────────────────────── */}
        {!loading && totalCount > 0 && (
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={1} mt={1.5} px={0.5}>
            <Typography variant="caption" color="text.secondary">
              Page {page} of {totalPages} · {totalCount} B2B invoices{hasFilters ? " (filtered)" : ""}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
            />
            <Typography variant="caption" color="text.secondary">
              {serialOffset + 1}–{Math.min(serialOffset + pageSize, totalCount)} of {totalCount}
            </Typography>
          </Stack>
        )}
      </Container>

      <Box sx={{ width: "100%", mt: isMobile ? 4 : 6 }}>
        <Footer />
      </Box>
    </>
  );
};

export default B2BSalesRegisterGST;